import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService, apiErrorMessage } from '@/lib/api';
import { JobContactAssignmentDTO, PhoneNumberDTO } from '@/types';
import { X, Send, MessageSquare, Phone, Pencil, RotateCcw, Paperclip, FileText, Loader2, AlertCircle, AlertTriangle } from 'lucide-react';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { useNotifications, notificationService } from './ui/NotificationSystem';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { useGlobalCompany } from '@/hooks/useGlobalCompany';
import { usePermissions } from '@/hooks/usePermissions';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { canSendSmsFrom, formatPhoneNumber, formatVoipNumberLabel, smsCampaignWarning, stripPhoneToDigits } from '@/lib/phoneUtils';
import { PhoneInput } from './ui/PhoneInput';
import {
    MMS_ACCEPT,
    MMS_MAX_FILES,
    PendingSmsFile,
    revokeSmsPreviewUrls,
    uploadMmsFiles,
} from './SmsConversationPanel';

interface SendSmsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSent?: () => void;
    recipientPhone: string;
    recipientName: string;
    recipientType: 'Technician' | 'Client';
    initialMessage?: string;
    jobId?: string;
    /** Estimate share/SMS has no job. Technicians pass this instead of jobId. */
    estimateId?: string;
    /** Opaque masked contact reference ("contact:{id}:{primary|additional}"). When present,
     * the backend resolves the real destination — no real phone is sent from the client. */
    toContactRef?: string;
    /** When true the message is a fixed template the user cannot edit (invoice/estimate share). */
    lockMessage?: boolean;
    /** Shown above the message — e.g. a template variable the job had no value for. */
    warning?: string;
}

function pickDefaultPhoneNumberId(numbers?: PhoneNumberDTO[]): string {
    if (!numbers?.length) return '';
    const smsCapable = numbers.filter(canSendSmsFrom);
    return smsCapable[0]?.id ?? '';
}

function contactRef(id: string, which: 'primary' | 'additional'): string {
    return `contact:${id}:${which}`;
}

/** When job contacts are last-4-masked, send via opaque ref — never the incomplete number. */
function findMaskedContactRef(
    contacts: JobContactAssignmentDTO[] | undefined,
    dest: string,
    fallbackPhone: string,
): string | undefined {
    if (!contacts?.length) return undefined;
    if (dest.startsWith('contact:')) return dest;
    const needle = dest || fallbackPhone;
    const needleDigits = stripPhoneToDigits(needle);
    for (const { contact: c } of contacts) {
        if (!c?.phone_masked || !c.id) continue;
        if (c.phone && (c.phone === needle || (needleDigits && stripPhoneToDigits(c.phone) === needleDigits)))
            return contactRef(c.id, 'primary');
        if (c.additional_phone && (c.additional_phone === needle || (needleDigits && stripPhoneToDigits(c.additional_phone) === needleDigits)))
            return contactRef(c.id, 'additional');
    }
    const first = contacts.find((a) => a.contact?.phone_masked)?.contact;
    return first?.id ? contactRef(first.id, 'primary') : undefined;
}

export function SendSmsModal({
    isOpen,
    onClose,
    onSent,
    recipientPhone,
    recipientName,
    recipientType,
    initialMessage,
    jobId,
    estimateId,
    toContactRef,
    lockMessage = false,
    warning,
}: SendSmsModalProps) {
    const { addNotification } = useNotifications();
    const { selectedCompanyId } = useGlobalCompany();
    const { isFeatureAllowed } = usePermissions();
    // Editing a locked 10DLC template means composing free text, which is the "custom SMS"
    // capability. Plans without it can still send the template as-is.
    // (This replaces a temporary hardcoded allow-list of two organization IDs.)
    const canEditTemplate = isFeatureAllowed('messages.sms.custom');
    const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string>('');
    const [message, setMessage] = useState(initialMessage || '');
    const [destinationPhone, setDestinationPhone] = useState(toContactRef ?? recipientPhone);
    // User picked "Enter manually..." - send to the typed number, never swap it for a contact ref.
    const [customDest, setCustomDest] = useState(false);
    // When the message is a locked template, the user must explicitly unlock it to edit.
    const [unlocked, setUnlocked] = useState(false);
    const [showEditConfirm, setShowEditConfirm] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<PendingSmsFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const prevIsOpenRef = useRef(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isLocked = lockMessage && !unlocked;
    const layer = useModalLayer({
        enabled: isOpen,
        onClose,
        historyState: { sendSmsModal: true },
    });

    // Fetch Job if jobId is provided
    const { data: jobData } = useQuery({
        queryKey: ['job', jobId],
        queryFn: () => apiService.getJobById(jobId!),
        enabled: isOpen && !!jobId,
    });
    const contactsMasked = !!jobData?.contacts?.some((a: JobContactAssignmentDTO) => a.contact?.phone_masked);

    // Fetch phone numbers
    const { data: voipNumbers, isLoading: numbersLoading } = useQuery({
        // With a job, the server puts its line first (the default below), so the customer keeps one number.
        queryKey: ['voip-phone-numbers', selectedCompanyId, jobId ?? null],
        queryFn: () => apiService.getPhoneNumbers(jobId),
        enabled: isOpen && !!selectedCompanyId,
    });

    const campaignWarning = smsCampaignWarning(voipNumbers?.find((n) => n.id === selectedPhoneNumberId));

    const availableNumbers = useMemo(() => {
        const numbers: { label: string; value: string }[] = [];
        const seen = new Set<string>();

        const addNumber = (label: string, value: string) => {
            if (value && !seen.has(value)) {
                seen.add(value);
                numbers.push({ label, value });
            }
        };

        if (jobData?.contacts && recipientType === 'Client') {
            jobData.contacts.forEach((assignment: JobContactAssignmentDTO) => {
                const contact = assignment.contact;
                if (!contact) return;

                if (contact.phone_masked) {
                    if (contact.phone) {
                        addNumber(`${contact.name} (Main) - ${contact.phone}`, contactRef(contact.id, 'primary'));
                    }
                    if (contact.additional_phone) {
                        addNumber(`${contact.name} (Additional) - ${contact.additional_phone}`, contactRef(contact.id, 'additional'));
                    }
                } else {
                    if (contact.phone) {
                        addNumber(`${contact.name} (Main) - ${formatPhoneNumber(contact.phone)}`, contact.phone);
                    }
                    if (contact.additional_phone) {
                        addNumber(`${contact.name} (Additional) - ${formatPhoneNumber(contact.additional_phone)}`, contact.additional_phone);
                    }
                }
            });
        } else if (jobData?.assigned_technician && recipientType === 'Technician') {
            if (jobData.assigned_technician.phone) {
                addNumber(`${jobData.assigned_technician.name} - ${formatPhoneNumber(jobData.assigned_technician.phone)}`, jobData.assigned_technician.phone);
            }
        }

        // Masked recipient not covered by job contacts (e.g. estimate share): offer the ref by name.
        if (toContactRef) addNumber(recipientName, toContactRef);

        // Don't add a last-4 display number as a sendable destination when contacts are masked.
        if (recipientPhone && !seen.has(recipientPhone) && !contactsMasked && !toContactRef) {
            addNumber(`${recipientName} - ${formatPhoneNumber(recipientPhone)}`, recipientPhone);
        }

        return numbers;
    }, [jobData, recipientType, recipientPhone, recipientName, contactsMasked, toContactRef]);
    const showCustomInput = customDest || !availableNumbers.some((n) => n.value === destinationPhone);

    const selectedNumber = voipNumbers?.find((n) => n.id === selectedPhoneNumberId);
    const supportsMms = selectedNumber?.capabilitiesMms ?? false;

    // Send SMS mutation
    const sendSmsMutation = useMutation({
        mutationFn: async ({
            phoneNumberId,
            toPhone,
            text,
            attachments,
        }: {
            phoneNumberId: string;
            toPhone: string;
            text: string;
            attachments?: PendingSmsFile[];
        }) => {
            const resolvedRef = customDest
                ? undefined
                : toPhone.startsWith('contact:')
                    ? toPhone
                    : findMaskedContactRef(jobData?.contacts, toPhone, recipientPhone);
            const objectKeys = attachments?.map((a) => a.objectKey);
            const contentTypes = attachments?.map((a) => a.contentType);
            let result;
            if (resolvedRef) {
                result = await apiService.sendMessageV2({
                    fromPhoneNumberId: phoneNumberId,
                    toContactRef: resolvedRef,
                    jobId,
                    estimateId,
                    body: text.trim() || undefined,
                    mediaObjectKeys: objectKeys,
                    mediaObjectContentTypes: contentTypes,
                });
            } else {
                const digits = stripPhoneToDigits(toPhone);
                const toE164 = toPhone.trimStart().startsWith('+') ? `+${digits}` : `+1${digits}`;
                result = await apiService.sendMessageV2({
                    fromPhoneNumberId: phoneNumberId,
                    toE164,
                    jobId,
                    estimateId,
                    body: text.trim() || undefined,
                    mediaObjectKeys: objectKeys,
                    mediaObjectContentTypes: contentTypes,
                });
            }
            if (result.status === 'quota_blocked') {
                throw new Error(result.reason ?? 'Monthly outbound quota exhausted');
            }
            if (result.status === 'failed' || result.status === 'invalid') {
                throw new Error(result.errorMessage ?? result.reason ?? 'Send failed.');
            }
        },
        onSuccess: () => {
            addNotification(notificationService.success('SMS sent successfully'));
            onSent?.();
            layer.onClose();
        },
        onError: (error: unknown) => {
            clientLogger.error('Failed to send SMS:', error);
            addNotification(
                notificationService.error('Failed to send SMS', apiErrorMessage(error, 'Please try again.'))
            );
        },
    });

    // Reset form when the modal opens (not on every prop change while open).
    useEffect(() => {
        const justOpened = isOpen && !prevIsOpenRef.current;
        prevIsOpenRef.current = isOpen;

        if (justOpened) {
            setMessage(initialMessage || '');
            setDestinationPhone(toContactRef ?? recipientPhone);
            setCustomDest(false);
            setUnlocked(false);
            setShowEditConfirm(false);
            setSelectedPhoneNumberId(pickDefaultPhoneNumberId(voipNumbers));
            setPendingFiles((prev) => {
                revokeSmsPreviewUrls(prev);
                return [];
            });
            setIsUploading(false);
            setUploadError(null);
            sendSmsMutation.reset();
        }
    }, [isOpen, initialMessage, recipientPhone, toContactRef, jobId, voipNumbers, sendSmsMutation]);

    // Job load is async — swap a last-4 destination for the contact ref before send.
    useEffect(() => {
        if (!isOpen || !contactsMasked || customDest) return;
        const ref = toContactRef || findMaskedContactRef(jobData?.contacts, destinationPhone, recipientPhone);
        if (ref && !destinationPhone.startsWith('contact:')) setDestinationPhone(ref);
    }, [isOpen, contactsMasked, customDest, jobData, destinationPhone, recipientPhone, toContactRef]);

    // If VoIP numbers load after open, pick the default from-number once.
    useEffect(() => {
        if (!isOpen || selectedPhoneNumberId) return;
        const defaultId = pickDefaultPhoneNumberId(voipNumbers);
        if (defaultId) setSelectedPhoneNumberId(defaultId);
    }, [isOpen, voipNumbers, selectedPhoneNumberId]);

    // Drop pending MMS files if the user switches to a number that can't send them.
    useEffect(() => {
        if (supportsMms) return;
        setPendingFiles((prev) => {
            if (!prev.length) return prev;
            revokeSmsPreviewUrls(prev);
            return [];
        });
    }, [supportsMms]);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (!files.length) return;
        e.target.value = '';
        const remaining = MMS_MAX_FILES - pendingFiles.length;
        const toProcess = files.slice(0, remaining);
        if (!toProcess.length) return;

        setIsUploading(true);
        setUploadError(null);
        try {
            const uploaded = await uploadMmsFiles(toProcess);
            setPendingFiles((prev) => [...prev, ...uploaded]);
        } catch (err: unknown) {
            setUploadError(err instanceof Error ? err.message : 'File upload failed. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const removeFile = (objectKey: string) => {
        setPendingFiles((prev) => {
            const removed = prev.find((f) => f.objectKey === objectKey);
            if (removed) revokeSmsPreviewUrls([removed]);
            return prev.filter((f) => f.objectKey !== objectKey);
        });
    };

    const handleReset = () => {
        setMessage(initialMessage || '');
        setUnlocked(false);
    };

    const handleSend = () => {
        if (!selectedPhoneNumberId || !destinationPhone.trim()) return;
        if (!message.trim() && pendingFiles.length === 0) return;
        if (jobId && !jobData) return;
        sendSmsMutation.mutate({
            phoneNumberId: selectedPhoneNumberId,
            toPhone: destinationPhone,
            text: message,
            attachments: pendingFiles.length > 0 ? pendingFiles : undefined,
        });
    };

    const canSend =
        selectedPhoneNumberId &&
        destinationPhone.trim() &&
        (message.trim() || pendingFiles.length > 0) &&
        !sendSmsMutation.isPending &&
        !isUploading &&
        !(jobId && !jobData);

    if (!isOpen) return null;

    return (
        <LayerDepthContext.Provider value={layer.depth}>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={layer.style}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <MessageSquare className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Send SMS to {recipientType}
                            </h2>
                            <p className="text-sm text-gray-500">{recipientName}</p>
                        </div>
                    </div>
                    <button
                        onClick={layer.onClose}
                        className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                    {/* From Number Selector */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Send From
                        </label>
                        {numbersLoading ? (
                            <div className="flex items-center gap-2 py-2">
                                <LoadingSpinner />
                                <span className="text-sm text-gray-500">Loading numbers...</span>
                            </div>
                        ) : voipNumbers && voipNumbers.length > 0 ? (
                            <select
                                value={selectedPhoneNumberId}
                                onChange={(e) => setSelectedPhoneNumberId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            >
                                {voipNumbers.filter(canSendSmsFrom).map((num: PhoneNumberDTO) => (
                                    <option key={num.id} value={num.id}>
                                        {formatVoipNumberLabel(num.e164Number, num.displayName)}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                                No VoIP numbers configured. Please add a number in Settings → VoIP.
                            </div>
                        )}
                    </div>

                    {/* Destination Number */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            To
                        </label>
                        {availableNumbers.length > 0 ? (
                            <div className="space-y-2">
                                <select
                                    value={showCustomInput ? 'custom' : destinationPhone}
                                    onChange={(e) => {
                                        const custom = e.target.value === 'custom';
                                        setCustomDest(custom);
                                        setDestinationPhone(custom ? '' : e.target.value);
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white mt-1"
                                >
                                    {availableNumbers.map((num, idx) => (
                                        <option key={idx} value={num.value}>
                                            {num.label}
                                        </option>
                                    ))}
                                    <option value="custom">Enter manually...</option>
                                </select>

                                {showCustomInput && (
                                    <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg mt-2">
                                        <Phone className="h-4 w-4 text-gray-400" />
                                        <PhoneInput
                                            value={destinationPhone}
                                            onChange={(val) => setDestinationPhone(val)}
                                            className="w-full outline-none"
                                            placeholder="Enter custom destination phone"
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg">
                                <Phone className="h-4 w-4 text-gray-400" />
                                <PhoneInput
                                    value={destinationPhone}
                                    onChange={(val) => setDestinationPhone(val)}
                                    className="w-full outline-none"
                                    placeholder="Enter destination phone"
                                />
                            </div>
                        )}
                    </div>

                    {/* Message Input */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                                Message
                            </label>
                            {lockMessage && (
                                isLocked ? (
                                    canEditTemplate && (
                                        <button
                                            type="button"
                                            onClick={() => setShowEditConfirm(true)}
                                            className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                            Edit
                                        </button>
                                    )
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleReset}
                                        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
                                    >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Reset
                                    </button>
                                )
                            )}
                        </div>
                        {[warning, campaignWarning].filter(Boolean).map((w) => (
                            <div key={w} className="mb-2 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px" />
                                <span>{w}</span>
                            </div>
                        ))}
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            readOnly={isLocked}
                            placeholder="Type your message..."
                            rows={4}
                            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${isLocked ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}`}
                            maxLength={1600}
                        />
                        {supportsMms && (
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={MMS_ACCEPT}
                                className="hidden"
                                onChange={handleFileSelect}
                            />
                        )}
                        {pendingFiles.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                                {pendingFiles.map((f) => (
                                    <div key={f.objectKey} className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 overflow-hidden">
                                        {f.previewUrl ? (
                                            <img src={f.previewUrl} alt={f.name} className="h-10 w-10 object-cover flex-shrink-0" />
                                        ) : (
                                            <div className="flex items-center justify-center h-10 w-10 flex-shrink-0">
                                                <FileText className="w-4 h-4" />
                                            </div>
                                        )}
                                        <span className="max-w-[100px] truncate">{f.name}</span>
                                        <button
                                            type="button"
                                            onClick={() => removeFile(f.objectKey)}
                                            className="ml-0.5 mr-1.5 text-blue-400 hover:text-blue-700"
                                            title="Remove"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {isUploading && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Uploading…
                            </div>
                        )}
                        {uploadError && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{uploadError}</span>
                                <button type="button" onClick={() => setUploadError(null)} className="ml-auto text-red-400 hover:text-red-600">
                                    <X className="w-3 h-3" />
                                </button>
                            </div>
                        )}
                        <div className="flex items-center justify-between mt-1">
                            {supportsMms ? (
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isUploading || pendingFiles.length >= MMS_MAX_FILES}
                                    className={`flex items-center gap-1 text-xs font-medium disabled:opacity-40 ${pendingFiles.length > 0 ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                    <Paperclip className="h-3.5 w-3.5" />
                                    Attach file
                                </button>
                            ) : (
                                <span />
                            )}
                            <span className="text-xs text-gray-400">{message.length}/1600</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 keyboard-aware-footer">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!canSend}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {sendSmsMutation.isPending ? (
                            <>
                                <LoadingSpinner />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4" />
                                Send SMS
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>

        <ConfirmDialog
            isOpen={showEditConfirm}
            onClose={() => setShowEditConfirm(false)}
            onConfirm={() => setUnlocked(true)}
            title="Edit message?"
            message={
                <>
                    This message is pre-approved for your 10DLC registered texting campaign. Changing it may cause your message to be blocked, not delivered, or result in carrier fines.
                    <br />
                    <br />
                    For more information about 10DLC campaigns:{' '}
                    <a
                        href="https://servicestrike.com/understanding-10dlc.html"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 underline"
                    >
                        click here
                    </a>
                </>
            }
            confirmText="Edit anyway"
            confirmButtonClassName="bg-blue-600 hover:bg-blue-700 text-white"
        />
        </LayerDepthContext.Provider>
    );
}

export default SendSmsModal;
